import { InfoCircle } from "iconsax-react";
import { Input, OnboardingTooltip } from "@/shared/ui";
import { sanitizeMoney } from "../../../utils";

type FieldKey = string | number;

interface AmountRow<K extends FieldKey> {
  field: K;
  label: string;
  remaining: string;
  planned?: string;
}

interface Props<K extends FieldKey> {
  rows: AmountRow<K>[];
  values: Record<K, string>;
  onChange: (field: K, value: string) => void;
  errors?: Partial<Record<K, string>>;
  secondaryHeader: string;
  secondaryTooltip?: string;
  inputHeader: string;
  t: (key: string) => string;
}

export default function RedistributeAmountsTable<K extends FieldKey>({
  rows,
  values,
  onChange,
  errors = {} as Partial<Record<K, string>>,
  secondaryHeader,
  secondaryTooltip,
  inputHeader,
  t,
}: Props<K>) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 pb-2 border-b surface-base-stroke">
        <span className="flex-1 text-body-regular-xs content-base-secondary">{t("redistribute.colArticle")}</span>
        <div className="w-32 flex items-center justify-end gap-1 content-base-secondary">
          <span className="text-body-regular-xs content-base-secondary">{secondaryHeader}</span>
          {secondaryTooltip && (
            <OnboardingTooltip
              direction="top"
              align="right"
              className="inline-flex items-center cursor-help content-base-secondary"
              content={
                <span className="block max-w-[240px] text-body-regular-2xs content-on-background-neutral">
                  {secondaryTooltip}
                </span>
              }>
              <InfoCircle size={14} color="currentColor" />
            </OnboardingTooltip>
          )}
        </div>
        <span className="w-40 text-right text-body-regular-xs content-base-secondary">{inputHeader}</span>
      </div>

      {rows.map((row) => {
        const errorKey = errors[row.field];
        return (
          <div key={String(row.field)} className="flex items-center gap-3 py-2.5 border-b surface-base-stroke">
            <span className="flex-1 text-body-regular-sm content-base-primary">{row.label}</span>
            <div className="w-32 flex flex-col items-end">
              <span className="text-body-bold-sm content-base-primary">{row.remaining}</span>
              {row.planned !== undefined && (
                <span className="text-body-regular-2xs content-base-low">{row.planned}</span>
              )}
            </div>
            <div className="w-40">
              <Input
                isSmall
                onlyNumber
                placeholder="0"
                className="text-right"
                value={values[row.field] ?? ""}
                onChange={(e) => onChange(row.field, sanitizeMoney(e.target.value))}
                error={errorKey ? t(errorKey) : undefined}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
